/**
 * FloatingChatWidget - DM chat popup for NLT Radio
 * Ported from main NLT app with NLT Radio styling
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, Minus, Send, ChevronUp, MessageCircle, ShoppingBag } from 'lucide-react';
import { messagesApi } from '../api/messages';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const FloatingChatWidget = ({ 
  chatUser, 
  onClose, 
  onMinimize, 
  isMinimized = false,
  initialMessages = [],
  initialDraft = '',
  product = null,
  onNewMessage
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState(initialMessages);
  const [newMessage, setNewMessage] = useState(initialDraft || '');
  const [attachProduct, setAttachProduct] = useState(product || null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load messages on mount and set up polling
  useEffect(() => {
    if (!isMinimized && chatUser?.id) {
      loadMessages();
      const interval = setInterval(loadMessages, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [chatUser?.id, isMinimized]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  // Focus input when expanded
  useEffect(() => {
    if (!isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isMinimized]);

  const loadMessages = async () => {
    if (!chatUser?.id) return;
    
    try {
      const response = await messagesApi.getThread(chatUser.id);
      setMessages(response.messages || []);
      setError(null);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to load messages:', err);
      }
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setError(null);
    
    try {
      await messagesApi.send({
        toUserId: chatUser.id,
        text: newMessage.trim(),
        ...(attachProduct ? { product: attachProduct } : {}),
      });
      setNewMessage('');
      setAttachProduct(null);
      loadMessages();
      if (onNewMessage) {
        onNewMessage(chatUser.id, newMessage);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const hasUnread = messages.some(m => !m.read && m.fromUserId !== user?.id);

  // Minimized state - sleek bar
  if (isMinimized) {
    return (
      <div 
        className="group cursor-pointer"
        onClick={onMinimize}
        data-testid={`chat-widget-minimized-${chatUser?.id}`}
      >
        <div className="w-56 bg-[#0A0A0F]/95 backdrop-blur-xl border border-white/10 rounded-full shadow-lg px-3 py-2 flex items-center gap-2 hover:border-[#00F0FF]/30 transition-all duration-200">
          {/* Avatar */}
          {chatUser?.profilePhoto ? (
            <img 
              src={chatUser.profilePhoto} 
              alt={chatUser.name}
              className="w-7 h-7 rounded-full object-cover ring-1 ring-[#00F0FF]/30"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center text-white text-xs font-bold">
              {chatUser?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          
          {/* Name */}
          <span className="flex-1 text-white/90 text-sm font-medium truncate">
            {chatUser?.name || 'User'}
          </span>
          
          {/* Unread dot */}
          {hasUnread && (
            <span className="w-2 h-2 bg-[#FF007F] rounded-full animate-pulse shadow-lg shadow-[#FF007F]/50" />
          )}
          
          {/* Expand icon */}
          <ChevronUp className="w-4 h-4 text-white/50 group-hover:text-[#00F0FF] transition-colors" />
          
          {/* Close */}
          <button 
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-3.5 h-3.5 text-white/50 hover:text-white/90" />
          </button>
        </div>
      </div>
    );
  }

  // Expanded state - NLT Radio styled
  return (
    <div 
      className="w-80 bg-[#0A0A0F]/98 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
      style={{ height: '420px' }}
      data-testid={`chat-widget-${chatUser?.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#00F0FF]/10 to-[#FF007F]/10">
        {/* Avatar */}
        <div 
          className="cursor-pointer"
          onClick={() => navigate(`/profile/${chatUser?.id}`)}
        >
          {chatUser?.profilePhoto ? (
            <img 
              src={chatUser.profilePhoto} 
              alt={chatUser.name}
              className="w-10 h-10 rounded-full object-cover ring-2 ring-[#00F0FF]/30 hover:ring-[#00F0FF]/50 transition-all"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center text-white font-bold ring-2 ring-[#00F0FF]/30">
              {chatUser?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
        </div>
        
        {/* Name & Username */}
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => navigate(`/profile/${chatUser?.id}`)}
        >
          <p className="text-white font-semibold text-sm truncate hover:text-[#00F0FF] transition-colors">
            {chatUser?.name || 'User'}
          </p>
          {chatUser?.username && (
            <p className="text-white/40 text-xs truncate">
              @{chatUser.username}
            </p>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          <button 
            onClick={onMinimize}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Minimize"
            data-testid="chat-minimize-btn"
          >
            <Minus className="w-4 h-4 text-white/50 hover:text-white/90" />
          </button>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Close"
            data-testid="chat-close-btn"
          >
            <X className="w-4 h-4 text-white/50 hover:text-white/90" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00F0FF]/20 to-[#FF007F]/20 flex items-center justify-center mb-3 ring-1 ring-white/10">
              <MessageCircle className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <p className="text-white/60 font-medium">{chatUser?.name}</p>
            <p className="text-white/30 text-sm mt-1">Start a conversation</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwn = msg.fromUserId === user?.id;
            return (
              <div 
                key={msg.id || idx} 
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] px-4 py-2 ${
                    isOwn 
                      ? 'bg-gradient-to-r from-[#00F0FF] to-[#00D4FF] text-black rounded-2xl rounded-br-md' 
                      : 'bg-white/10 text-white/90 rounded-2xl rounded-bl-md'
                  }`}
                >
                  {msg.product && msg.product.name && (
                    <div
                      className={`flex items-center gap-2 mb-1.5 p-1.5 rounded-lg cursor-pointer ${isOwn ? 'bg-black/10' : 'bg-white/10'}`}
                      onClick={() => navigate('/marketplace')}
                      data-testid={`chat-product-chip-${msg.id || idx}`}
                    >
                      {msg.product.image ? (
                        <img src={msg.product.image} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                      ) : (
                        <div className={`w-9 h-9 rounded flex items-center justify-center shrink-0 ${isOwn ? 'bg-black/10' : 'bg-white/10'}`}>
                          <ShoppingBag className="w-4 h-4 opacity-70" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className={`text-[11px] font-semibold truncate ${isOwn ? 'text-black' : 'text-white'}`}>{msg.product.name}</p>
                        {msg.product.price != null && (
                          <p className={`text-[10px] ${isOwn ? 'text-black/60' : 'text-[#00F0FF]'}`}>${Number(msg.product.price).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                  )}
                  <p className="text-sm break-words leading-relaxed">{msg.text}</p>
                  <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                    <p className={`text-[10px] ${isOwn ? 'text-black/50' : 'text-white/40'}`}>
                      {formatTime(msg.createdAt)}
                    </p>
                    {isOwn && msg.read && (
                      <span className="text-[10px] text-black/50">• Read</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-4 py-2 bg-red-500/20 border-t border-red-500/30">
          <p className="text-red-400 text-xs">{error}</p>
        </div>
      )}

      {/* Input Area */}
      <div className="px-4 py-3 border-t border-white/10 bg-black/30">
        {attachProduct && (
          <div className="flex items-center gap-2 mb-2 p-2 rounded-xl bg-white/5 border border-[#00F0FF]/20" data-testid="chat-attached-product">
            {attachProduct.image ? (
              <img src={attachProduct.image} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center shrink-0">
                <ShoppingBag className="w-4 h-4 text-white/60" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[11px] text-white/50">About this product</p>
              <p className="text-xs text-white font-medium truncate">{attachProduct.name}</p>
            </div>
            <button
              onClick={() => setAttachProduct(null)}
              className="p-1 rounded-full hover:bg-white/10 text-white/50 shrink-0"
              data-testid="chat-remove-attached-product"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00F0FF]/50 focus:bg-white/10 transition-all"
            data-testid="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className="p-2.5 bg-gradient-to-r from-[#00F0FF] to-[#00D4FF] text-black rounded-full hover:shadow-lg hover:shadow-[#00F0FF]/25 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FloatingChatWidget;
